import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { useHandleShowDialogs } from 'forms/hooks';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import AutocompleteCustom from 'components/atoms/GefMui/AutocompleteCustom';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelistProvider } from 'components/index';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import { saveNewCampaign } from 'features/views/campaignList/newCampaignModal/actions';
import { campaignTypeOptions } from 'features/views/campaignList/newCampaignModal/constants';
import { FIELDS, NEW_CAMPAIGN_FORM_NAME } from 'features/views/campaignList/newCampaignModal/form';
import { getRoutePath } from 'utils/routeHelper';

export const Form = ({ campaignChannels = [] }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const formData = useFormData();

	const handleSaveNewCampaign = useCallback(async () => {
		const newCampaignHasBeenSucessfullyCreated = await dispatch(saveNewCampaign());
		if (newCampaignHasBeenSucessfullyCreated) {
			const { pathname, state } = getRoutePath(
				'campaignSettings',
				{ campaignId: newCampaignHasBeenSucessfullyCreated?.body?.id },
				true,
			);
			navigate(pathname, { state });
		}
	}, [dispatch, navigate]);

	const handleShowDialogsForSubmit = useHandleShowDialogs({
		page: NEW_CAMPAIGN_FORM_NAME,
		continueFn: handleSaveNewCampaign,
	});

	useEffect(
		() => () => {
			dispatch(resetErrorsByPage({ page: NEW_CAMPAIGN_FORM_NAME }));
		},
		[dispatch],
	);

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<TextField
							label={getLabelMessage('campaignsList:newCampaignName')}
							size="small"
							fieldName={FIELDS.campaignName}
							validationDisplay={!formData?.campaignName ? validationDisplayTypes.HIDDEN : undefined}
							required
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={4}>
						<AutocompleteCustom
							size="small"
							fieldName={FIELDS.channel}
							label={getLabelMessage('campaignsList:channel')}
							options={campaignChannels}
							validationDisplay={!formData?.channel ? validationDisplayTypes.HIDDEN : undefined}
							required
							labelPropName="description"
							valuePropName="id"
						/>
					</Grid>
					<Grid size={4}>
						<TextField
							label={getLabelMessage('campaignsList:communicationName')}
							size="small"
							fieldName={FIELDS.communicationName}
							validationDisplay={!formData?.communicationName ? validationDisplayTypes.HIDDEN : undefined}
							required
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={4}>
						<AutocompleteCustom
							size="small"
							fieldName={FIELDS.campaignType}
							label={getLabelMessage('campaignsList:campaignType')}
							options={campaignTypeOptions}
							labelPropName="description"
							valuePropName="id"
							validationDisplay={!formData?.campaignType ? validationDisplayTypes.HIDDEN : undefined}
							required
						/>
					</Grid>
					<Grid size={4}>
						<DatePickerFullWidth
							label={getLabelMessage('campaignsList:campaignStartDate')}
							fieldName={FIELDS.startDate}
							validationDisplay={!formData?.startDate ? validationDisplayTypes.HIDDEN : undefined}
							required
						/>
					</Grid>
					<Grid size={4}>
						<DatePickerFullWidth
							label={getLabelMessage('campaignsList:campaignEndDate')}
							fieldName={FIELDS.endDate}
							validationDisplay={!formData?.endDate ? validationDisplayTypes.HIDDEN : undefined}
							required
						/>
					</Grid>
					<Grid size={12}>
						<Grid container justifyContent="flex-end">
							<Grid size={3}>
								<SubmitButton
									variant="contained"
									onClick={handleSaveNewCampaign}
									onClickError={handleShowDialogsForSubmit}
									fullWidth
									text={getLabelMessage('common:save')}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

Form.propTypes = {
	campaignChannels: PropTypes.array,
};

export default codelistProvider(codelists.campaignChannels, 'campaignChannels')(Form);
